import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, IconButton, InputAdornment, Paper, Stack, TextField, Tooltip } from '@mui/material';
import { ChangeEvent, FormEvent, ReactElement, useEffect, useState } from 'react';
import {
  DriveOnDriverInputPatchDto,
  DriveOnDriverInputPatchDtoInputType,
  DriveOnTaskDriverInput,
  DriveOnDisplayableTaskDto as DriveOnTask,
  NullableOptionalPatchDriveOnTaskDriverInput,
  DriveOnStatusDto,
} from '@/generated/model';
import { getLatestDriveOnDriverInput, patchDriverInput } from '@/generated/client/driveon-api/driveon-api';
import { useDriveOnContext } from '@/contexts/DriveOnContext';
import { ChooseTaskDialog } from '@/components/logbook/task/ChooseTaskDialog';
import { TaskChooser } from '@/components/logbook/task/TaskChooser';
import { DriveOnTaskDriverInputType } from '@/generated/model/driveOnTaskDriverInputType';
import { Add, Remove } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useNotificationContext } from '@/contexts/NotificationCtx';
import { DriveOnDriverInputInputType as InputType } from '@/generated/model/driveOnDriverInputInputType';
import { Logbook } from '@/model/Logbook';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { VehicleDialog } from '@/components/logbook/target/TargetConfirmationDialog';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import { AdhocTaskChooser } from '@/components/logbook/task/AdhocTaskChooser';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import './LogbookForm.css';

dayjs.locale('hu');

const STRING_DEFAULT_VALUE: string = '';
const NUMBER_DEFAULT_VALUE: number = -1;
const TIME_NUMBER_DEFAULT_VALUE: number = 0;
const TIME_DEFAULT_VALUE: Dayjs = dayjs().hour(0).minute(0).second(0).millisecond(0);

type LogbookStatus = typeof LogbookStatus;

const LogbookStatus = {
  TASK_SELECTED: 0,
  EDITING: 1,
  PROGRESS: 2,
} as const;

type FormDetails = {
  task: DriveOnTask | undefined;
  destination: string;
  productDescription: string;
  productWeight: number;
  personCount: number;
  trailer: string;
  workingHours: number;
  heating: number;
  comment: string;
};

type LogbookFormProps = {
  withChooseTaskDialog: boolean;
};

export function LogbookForm({ withChooseTaskDialog }: LogbookFormProps) {
  const driveOnCtx = useDriveOnContext();
  const notifyCtx = useNotificationContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [saveVisible, setSaveVisible] = useState<boolean>(false);
  const [chooseTaskDialog, setChooseTaskDialog] = useState<ReactElement | undefined>(undefined);
  const [selectedTask, setSelectedTask] = useState<DriveOnTask | undefined>(driveOnCtx.task);
  const [displayedTaskName, setDisplayedTaskName] = useState<string>('');
  const [status, setStatus] = useState<number>(LogbookStatus.PROGRESS);
  const [targetDialog, setTargetDialog] = useState<ReactElement | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<FormDetails>({
    task: driveOnCtx.task,
    destination: getDefaultValue(driveOnCtx.logbook?.destination, STRING_DEFAULT_VALUE),
    productDescription: getDefaultValue(driveOnCtx.logbook?.productDescription, STRING_DEFAULT_VALUE),
    productWeight: getDefaultValue(driveOnCtx.logbook?.productWeight, NUMBER_DEFAULT_VALUE),
    personCount: getDefaultValue(driveOnCtx.logbook?.personCount, NUMBER_DEFAULT_VALUE),
    trailer: getDefaultValue(driveOnCtx.logbook?.trailer, STRING_DEFAULT_VALUE),
    workingHours: getDefaultValue(driveOnCtx.logbook?.workingHours, TIME_NUMBER_DEFAULT_VALUE),
    heating: getDefaultValue(driveOnCtx.logbook?.heatingHours, TIME_NUMBER_DEFAULT_VALUE),
    comment: getDefaultValue(driveOnCtx.logbook?.comment, STRING_DEFAULT_VALUE),
  });
  const timePickerLocaleText = {
    cancelButtonLabel: t('general.cancel'),
    okButtonLabel: t('general.ok'),
    toolbarTitle: t('general.timepicker.title'),
  };

  function generateEgsEventusId(): string {
    return new Date().getTime() + '';
  }

  function setFormDetails(key: string, value: any) {
    setDetails((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function isDetailsChanged(): boolean {
    if (details.destination !== getDefaultValue(driveOnCtx.logbook?.destination, STRING_DEFAULT_VALUE)) {
      return true;
    }

    if (details.productDescription !== getDefaultValue(driveOnCtx.logbook?.productDescription, STRING_DEFAULT_VALUE)) {
      return true;
    }

    if (details.productWeight !== getDefaultValue(driveOnCtx.logbook?.productWeight, NUMBER_DEFAULT_VALUE)) {
      return true;
    }

    if (details.personCount !== getDefaultValue(driveOnCtx.logbook?.personCount, NUMBER_DEFAULT_VALUE)) {
      return true;
    }

    if (details.trailer !== getDefaultValue(driveOnCtx.logbook?.trailer, STRING_DEFAULT_VALUE)) {
      return true;
    }

    if (details.workingHours !== getDefaultValue(driveOnCtx.logbook?.workingHours, TIME_NUMBER_DEFAULT_VALUE)) {
      return true;
    }

    if (details.heating !== getDefaultValue(driveOnCtx.logbook?.heatingHours, TIME_NUMBER_DEFAULT_VALUE)) {
      return true;
    }

    if (details.comment !== getDefaultValue(driveOnCtx.logbook?.comment, STRING_DEFAULT_VALUE)) {
      return true;
    }

    return false;
  }

  function isEdited(): boolean {
    if (details.destination !== STRING_DEFAULT_VALUE) {
      return true;
    }

    // if (details.productDescription !== STRING_DEFAULT_VALUE) {
    //   return true;
    // }

    // if (details.productWeight !== NUMBER_DEFAULT_VALUE) {
    //   return true;
    // }

    // if (details.personCount !== NUMBER_DEFAULT_VALUE) {
    //   return true;
    // }

    if (details.trailer !== STRING_DEFAULT_VALUE) {
      return true;
    }

    if (details.workingHours !== TIME_NUMBER_DEFAULT_VALUE) {
      return true;
    }

    if (details.heating !== TIME_NUMBER_DEFAULT_VALUE) {
      return true;
    }

    if (details.comment !== STRING_DEFAULT_VALUE) {
      return true;
    }

    return false;
  }

  function getDefaultValue(value: any, defaultValue: Object): any {
    return value ? value : defaultValue;
  }

  useEffect(() => {
    const contextIsNotLoaded = !driveOnCtx.inputType && !driveOnCtx.logbook && !driveOnCtx.target && !driveOnCtx.task;
    async function reloadPageContent() {
      if (contextIsNotLoaded) {
        const result = await getLatestDriveOnDriverInput().catch((error) => {
          const data = error.response.data;
          if (data.exceptionClass.includes('UserNotInDriverInputException')) {
            notifyCtx?.showNotification('error', t('report.missingTarget'), undefined, 10);
            navigate('/menetlevel/jarmuvalaszto');
          }
        });

        if (result && result.status == 200) {
          const data = result.data;
          updateDriveOnContext(data);
          console.log('reloadPageContent(): Api called: getLatestDriveOnDriverInput');
        }
      }
    }

    function updateDriveOnContext(data: DriveOnStatusDto) {
      const driverInput = data.driverInput;
      const inputType = driverInput?.inputType;
      driveOnCtx.setInputType(inputType);

      if (inputType === InputType.TARGET_UNASSIGNED) {
        eraseContext();
      } else if (inputType === InputType.MIDNIGHT_CLEANUP) {
        eraseContext();
      } else if (inputType === InputType.KICKED) {
        eraseContext();
      } else {
        updateContext(data);
      }
    }

    function eraseContext() {
      driveOnCtx.setTarget(undefined);
      driveOnCtx.setTask(undefined);
      driveOnCtx.setLogbook(undefined);
      notifyCtx?.showNotification('error', t('bottomnav.confirmVehicle'), undefined, 5);
      navigate('/menetlevel/jarmuvalaszto');
    }

    function updateContext(data: DriveOnStatusDto) {
      updateDetails(data);
      const driverInput = data.driverInput;
      const task = data.task;
      const target = data?.driverInput?.target;
      driveOnCtx.setTarget(target);
      driveOnCtx.setTask(task);
      driveOnCtx.setLogbook({
        taskName: driverInput?.task?.name,
        destination: driverInput?.destination,
        productDescription: driverInput?.productDescription,
        productWeight: driverInput?.productWeight,
        personCount: driverInput?.personCount,
        trailer: driverInput?.trailer,
        workingHours: driverInput?.target.egs ? 0 : driverInput?.workingHours,
        heatingHours: driverInput?.target.egs ? 0 : driverInput?.heatingHours,
        comment: driverInput?.comment,
      } satisfies Logbook);
    }

    reloadPageContent();
  }, []);

  useEffect(() => {
    async function handleTaskRoute() {
      if (withChooseTaskDialog) {
        await handleTaskSelector();
      }
    }
    function handleTargetNotSelected() {
      if (driveOnCtx.inputType && !driveOnCtx.target) {
        notifyCtx?.showNotification('error', t('bottomnav.confirmVehicle'), undefined, 10);
        navigate('/menetlevel/jarmuvalaszto');
      }
    }
    handleTaskRoute();
    handleTargetNotSelected();
  }, []);

  useEffect(() => {
    function handleSaveButton() {
      if (isDetailsChanged()) {
        setStatus(LogbookStatus.EDITING);
      } else {
        if (isEdited()) {
          setStatus(LogbookStatus.PROGRESS);
        } else {
          setStatus(LogbookStatus.TASK_SELECTED);
        }
      }
      setSaveVisible(isDetailsChanged());
    }
    handleSaveButton();
  }, [details]);

  useEffect(() => {
    function setTaskName() {
      const taskName = selectedTask?.taskName;
      if (taskName) {
        setDisplayedTaskName(taskName);
      } else {
        setDisplayedTaskName('');
      }
    }
    setTaskName();
  }, [selectedTask]);

  function updateLogbook(data: DriveOnStatusDto) {
    const driverInput = data.driverInput;
    driveOnCtx?.setTask(data.task);
    driveOnCtx?.setLogbook({
      taskName: data.task?.taskName,
      destination: driverInput?.destination,
      productDescription: driverInput?.productDescription,
      productWeight: driverInput?.productWeight,
      personCount: driverInput?.personCount,
      trailer: driverInput?.trailer,
      workingHours: isEGSTarget() ? 0 : driverInput?.workingHours,
      heatingHours: isEGSTarget() ? 0 : driverInput?.heatingHours,
      comment: driverInput?.comment,
    } satisfies Logbook);
  }

  function updateDetails(data: DriveOnStatusDto) {
    const driverInput = data.driverInput;
    setFormDetails(
      'productDescription',
      driverInput?.productDescription ? driverInput?.productDescription : STRING_DEFAULT_VALUE
    );
    setFormDetails('productWeight', driverInput?.productWeight ? driverInput?.productWeight : NUMBER_DEFAULT_VALUE);
    setFormDetails('personCount', driverInput?.personCount ? driverInput?.personCount : NUMBER_DEFAULT_VALUE);
    setFormDetails('destination', driverInput?.destination ? driverInput?.destination : STRING_DEFAULT_VALUE);
    setFormDetails('trailer', driverInput?.trailer ? driverInput?.trailer : STRING_DEFAULT_VALUE);
    setFormDetails(
      'workingHours',
      isEGSTarget() ? 0 : driverInput?.workingHours ? driverInput?.workingHours : TIME_NUMBER_DEFAULT_VALUE
    );
    setFormDetails(
      'heating',
      isEGSTarget() ? 0 : driverInput?.heatingHours ? driverInput?.heatingHours : TIME_NUMBER_DEFAULT_VALUE
    );
    setFormDetails('comment', driverInput?.comment ? driverInput?.comment : STRING_DEFAULT_VALUE);
    setFormDetails('task', data.task);
    setDisplayedTaskName(driverInput?.task?.name ? driverInput?.task?.name : STRING_DEFAULT_VALUE);
    setSelectedTask(data.task);
  }

  async function checkPreConditions(): Promise<boolean> {
    if (!validateLicensePlate()) {
      notifyCtx?.showNotification('warning', t('menetlevel.choosetask.trailerwarning'), undefined, 5);
      return false;
    }
    if (isEGSTarget()) {
      return await checkEgsPreConditions();
    } else {
      return await checkDsoPreConditions();
    }
  }

  function validateLicensePlate(): boolean {
    const trailerPlate = details.trailer;
    if (trailerPlate == STRING_DEFAULT_VALUE) {
      return true;
    }
    const regex = /^[A-Z]{3,4}-?\d{3}$/;
    return regex.test(trailerPlate.toUpperCase());
  }

  async function checkEgsPreConditions(): Promise<boolean> {
    if (
      details.destination == STRING_DEFAULT_VALUE ||
      details.productDescription == STRING_DEFAULT_VALUE ||
      details.productWeight == NUMBER_DEFAULT_VALUE ||
      details.personCount == NUMBER_DEFAULT_VALUE
    ) {
      notifyCtx?.showNotification('warning', t('menetlevel.choosetask.warning'), undefined, 5);
      return false;
    } else {
      const response = await getLatestDriveOnDriverInput().catch((error) =>
        console.log('checkPreConditions(): Api failed: getLatestDriveOnDriverInput -> ', error)
      );

      if (response && response.status == 200) {
        console.log('checkPreConditions(): Api called: getLatestDriveOnDriverInput');
        const data = response.data;
        const driverInput = data.driverInput;
        const inputType = driverInput?.inputType;
        const target = driverInput?.target;

        if (inputType === InputType.TARGET_UNASSIGNED) {
          notifyCtx?.showNotification('info', t('home.activevehicle.chooseNicely'), undefined, 5);
          navigate('/menetlevel/jarmuvalaszto');
        } else if (inputType === InputType.MIDNIGHT_CLEANUP) {
          notifyCtx?.showNotification(
            'info',
            'Éjféli kijelentkezés miatt kiléptettük a ' + target?.name + ' járművéből',
            undefined,
            5
          );
          navigate('/fooldal');
        } else if (inputType === InputType.KICKED) {
          notifyCtx?.showNotification('info', t('home.activevehicle.kicked'), undefined, 5);
          navigate('/fooldal');
        } else {
          return true;
        }
      }
      return false;
    }
  }

  async function checkDsoPreConditions(): Promise<boolean> {
    if (
      details.task == undefined ||
      details.destination == STRING_DEFAULT_VALUE ||
      details.productDescription == STRING_DEFAULT_VALUE ||
      details.productWeight == NUMBER_DEFAULT_VALUE ||
      details.personCount == NUMBER_DEFAULT_VALUE
    ) {
      notifyCtx?.showNotification('warning', t('menetlevel.choosetask.warning'), undefined, 5);
      return false;
    } else {
      const response = await getLatestDriveOnDriverInput().catch((error) =>
        console.log('checkPreConditions(): Api failed: getLatestDriveOnDriverInput -> ', error)
      );

      if (response && response.status == 200) {
        console.log('checkPreConditions(): Api called: getLatestDriveOnDriverInput');
        const data = response.data;
        const driverInput = data.driverInput;
        const inputType = driverInput?.inputType;
        const target = driverInput?.target;

        if (inputType === InputType.TARGET_UNASSIGNED) {
          notifyCtx?.showNotification('info', t('home.activevehicle.chooseNicely'), undefined, 5);
          navigate('/menetlevel/jarmuvalaszto');
        } else if (inputType === InputType.MIDNIGHT_CLEANUP) {
          notifyCtx?.showNotification(
            'info',
            'Éjféli kijelentkezés miatt kiléptettük a ' + target?.name + ' járművéből',
            undefined,
            5
          );
          navigate('/fooldal');
        } else if (inputType === InputType.KICKED) {
          notifyCtx?.showNotification('info', t('home.activevehicle.kicked'), undefined, 5);
          navigate('/fooldal');
        } else {
          return true;
        }
      }
      return false;
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>, targetCardIdConfirmed: boolean) {
    event.preventDefault();
    const checkingOK = await checkPreConditions();
    if (checkingOK) {
      setLoading(true);
      const response = await patchDriverInput(detailsToDto(), { targetCardIdConfirmed })
        .catch((error) => {
          const response = error.response.data;
          if (
            error.response.status == 404 &&
            response.exceptionClass.includes('DriveOnAuthTemplateNotFoundException')
          ) {
            handleSubmit(event, true);
            return;
          }
          if (error.response.status != 409) {
            throw error;
          }
          const message = response.message;
          const errorType: string = response.exceptionClass;
          if (!errorType.includes('DriveOnTargetIdentificationException')) {
            throw error;
          }
          function onClose() {
            setTargetDialog(undefined);
          }
          async function onAccept() {
            handleSubmit(event, true);
            setTargetDialog(undefined);
          }
          setTargetDialog(
            <VehicleDialog onClose={onClose} onAccept={onAccept} message={message} target={driveOnCtx.target!} />
          );
        })
        .finally(() => setLoading(false));
      if (response && response.status == 200) {
        updateLogbook(response.data as DriveOnStatusDto);
        setStatus(LogbookStatus.PROGRESS);
        setSaveVisible(false);
        notifyCtx?.showNotification('success', t('menetlevel.logbook.submit.success'), undefined, 5);
        if (isEGSTarget()) {
          setFormDetails('workingHours', TIME_NUMBER_DEFAULT_VALUE);
          setFormDetails('heating', TIME_NUMBER_DEFAULT_VALUE);
        }
      } else {
        setStatus(LogbookStatus.EDITING);
      }
    }
  }

  function detailsToDto(): DriveOnDriverInputPatchDto {
    const egsTask: NullableOptionalPatchDriveOnTaskDriverInput = {
      value: {
        eventusId: { value: generateEgsEventusId(), applicable: true },
        costCenter: { value: 'costCenter', applicable: true },
        name: { value: 'name', applicable: true },
        type: DriveOnTaskDriverInputType.EGS,
      } satisfies DriveOnTaskDriverInput,
      applicable: true,
    };

    const dsoTask: NullableOptionalPatchDriveOnTaskDriverInput = {
      value: {
        eventusId: { value: details.task?.eventusId, applicable: true },
        costCenter: { value: details.task?.costCenter, applicable: true },
        name: { value: details.task?.taskName, applicable: true },
        type: isAdhocTask() ? DriveOnTaskDriverInputType.ADHOC : DriveOnTaskDriverInputType.DSO,
      } satisfies DriveOnTaskDriverInput,
      applicable: true,
    };

    return {
      createdTimeMicros: Date.now() * 1000,
      targetId: { value: driveOnCtx.target?.id, applicable: true },
      inputType: DriveOnDriverInputPatchDtoInputType.LOGBOOK_CHANGED,
      task: isEGSTarget() ? egsTask : dsoTask,
      destination: { value: details.destination, applicable: true },
      productDescription: { value: details.productDescription, applicable: true },
      productWeight: { value: details.productWeight, applicable: true },
      personCount: { value: details.personCount, applicable: true },
      trailer: { value: details.trailer, applicable: true },
      workingHours: { value: details.workingHours, applicable: true },
      heatingHours: { value: details.heating, applicable: true },
      comment: { value: details.comment, applicable: true },
    } satisfies DriveOnDriverInputPatchDto;
  }

  function add(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setFormDetails(event.target.name, event.target.value);
  }

  function validateNumberAndAdd(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, maxValue: number) {
    if (!isNaN(+event.target.value)) {
      if (event.target.value == '') {
        setFormDetails(event.target.name, NUMBER_DEFAULT_VALUE);
        console.log(details);
        return;
      } else if (+event.target.value < 0) {
        event.target.value = '0';
      } else if (+event.target.value > maxValue) {
        event.target.value = `${maxValue}`;
      }
      setFormDetails(event.target.name, +event.target.value);
    } else {
      if (event.target.value === STRING_DEFAULT_VALUE) {
        setFormDetails(event.target.name, NUMBER_DEFAULT_VALUE);
      }
    }
  }

  function convertTimeAndAdd(value: Dayjs | null, name: string) {
    setFormDetails(name, dayjsTimeToHours(value));
  }

  function showSaveButton() {
    if (saveVisible) {
      return (
        <div className="logbook-savebutton">
          <Stack className="save-stack" direction="row">
            <div className="changed">{t('menetlevel.logbook.changed')}</div>
            <Button type="submit" variant="contained" disabled={loading}>
              {t('menetlevel.logbook.save')}
            </Button>
          </Stack>
        </div>
      );
    }
  }

  async function handleTaskSelector() {
    const result = await showChooseTaskPopup();
    if (result) {
      setStatus(LogbookStatus.TASK_SELECTED);
      setFormDetails('task', result);
      resetValues();
      setSelectedTask(result);
    }
  }

  function resetValues() {
    // setFormDetails('productDescription', STRING_DEFAULT_VALUE);
    // setFormDetails('productWeight', NUMBER_DEFAULT_VALUE);
    // setFormDetails('personCount', NUMBER_DEFAULT_VALUE);
    setFormDetails('destination', STRING_DEFAULT_VALUE);
    setFormDetails('trailer', STRING_DEFAULT_VALUE);
    setFormDetails('workingHours', TIME_NUMBER_DEFAULT_VALUE);
    setFormDetails('heating', TIME_NUMBER_DEFAULT_VALUE);
    setFormDetails('comment', STRING_DEFAULT_VALUE);
  }

  async function showChooseTaskPopup(): Promise<DriveOnTask | undefined> {
    return new Promise((resolve) => {
      function onClose(task: DriveOnTask | undefined) {
        setChooseTaskDialog(undefined);
        resolve(task);
      }
      setChooseTaskDialog(<ChooseTaskDialog onClose={onClose} />);
    });
  }

  function renderNotSavedFlag() {
    return (
      <Button
        sx={{
          position: 'absolute',
          top: '-10px',
          right: '10px',
          height: '25px',
          zIndex: 1,
          border: '2px solid black',
          color: 'black',
          backgroundColor: 'rgb(255, 58, 58)',
        }}
      >
        {t('menetlevel.logbook.notsaved')}
      </Button>
    );
  }

  function renderSavedFlag() {
    return (
      <Button
        sx={{
          position: 'absolute',
          top: '-10px',
          right: '10px',
          height: '25px',
          zIndex: 1,
          border: '2px solid black',
          color: 'black',
          backgroundColor: 'rgb(43, 219, 37)',
        }}
      >
        {t('menetlevel.logbook.saved')}
      </Button>
    );
  }

  function showTaskStatus() {
    switch (status) {
      case LogbookStatus.TASK_SELECTED:
        return renderNotSavedFlag();

      case LogbookStatus.EDITING:
        return renderNotSavedFlag();

      case LogbookStatus.PROGRESS:
        return renderSavedFlag();
    }
  }

  function isAdhocTask() {
    if (driveOnCtx.task?.costCenter.includes('adhoc')) {
      return true;
    }
    return false;
  }

  //keprenyo tetejen
  function showSelectedTask() {
    if (selectedTask) {
      return (
        <>
          <Box sx={{ position: 'relative' }}>
            {showTaskStatus()}
            {!isAdhocTask() ? (
              <TaskChooser
                task={selectedTask}
                setTask={setSelectedTask}
                enableSelect={false}
                expanded={true}
                logbook={true}
              />
            ) : (
              <AdhocTaskChooser
                task={selectedTask}
                setTask={setSelectedTask}
                enableSelect={false}
                expanded={true}
                logbook={true}
              />
            )}
          </Box>
        </>
      );
    }
    return (
      <div className="notask">
        <div className="title">{t('menetlevel.logbook.chosentask.notask.title')}</div>
        <div className="description">{t('menetlevel.logbook.chosentask.notask.description')}</div>
      </div>
    );
  }

  function isEGSTarget(): boolean {
    if (driveOnCtx.target && driveOnCtx.target.egs) {
      return true;
    }
    return false;
  }

  function hoursToDayjsTime(hours: number | undefined): Dayjs {
    if (!hours) {
      return TIME_DEFAULT_VALUE;
    }
    const totalMinutes = Math.round(hours * 60);
    const hoursInt = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return dayjs().hour(hoursInt).minute(minutes).second(0).millisecond(0);
  }

  function dayjsTimeToHours(dayjsTime: Dayjs | null) {
    if (dayjsTime == null) {
      return 0;
    }
    return dayjsTime.hour() + dayjsTime.minute() / 60;
  }

  function TimeInputWithButtons(
    name: string,
    label: string,
    value: number,
    onChange: (e: Dayjs | null, name: string) => void
  ) {
    const changeTime = (minutesDelta: number) => {
      const dayjsTime = hoursToDayjsTime(value);
      setFormDetails(name, dayjsTimeToHours(dayjsTime.add(minutesDelta, 'minutes')));
    };

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label={label}
            name={name}
            value={hoursToDayjsTime(value)}
            onChange={(e) => onChange(e, label)}
            localeText={timePickerLocaleText}
            ampm={false}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
                id: 'outlined-start-adornment',
                placeholder: '0',
                InputProps: {
                  endAdornment: <InputAdornment position="end">óra</InputAdornment>,
                },
                size: 'small',
                InputLabelProps: { shrink: true },
              },
            }}
          />
        </LocalizationProvider>
        <IconButton onClick={() => changeTime(-30)} color="primary" disabled={value < 0.5}>
          <Remove />
        </IconButton>
        <IconButton onClick={() => changeTime(30)} color="primary" disabled={value >= 23.5}>
          <Add />
        </IconButton>
      </Stack>
    );
  }

  function handleFormEnterKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      setTimeout(() => {
        (event.target as HTMLInputElement).blur();
      }, 50);
    }
  }

  return (
    <>
      <div className="logbook-container">
        <div className="title">
          <span>{t('menetlevel.logbook.title')}</span>
        </div>
        {!isEGSTarget() && (
          <>
            <div className="task-title">{t('menetlevel.logbook.chosentask.title')}</div>
            <div className="task">{showSelectedTask()}</div>
          </>
        )}
        <div className="details-title">{t('menetlevel.logbook.details.title')}</div>
        <div className="description">{t('menetlevel.logbook.description')}</div>
        <Box
          className="form"
          component="form"
          sx={{ '& .MuiTextField-root': { marginBottom: 1, marginTop: 1, alignItems: 'flex-start', padding: 0 } }}
          noValidate
          autoComplete="off"
          onSubmit={(event) => handleSubmit(event, false)}
        >
          <Box className="field-container">
            {!isEGSTarget() && (
              <Paper variant="elevation" elevation={0} sx={{ display: 'flex', alignItems: 'center', border: '0px' }}>
                <TextField
                  fullWidth
                  id="outlined-start-adornment"
                  name="taskName"
                  label={t('menetlevel.logbook.form.taskName')}
                  variant="outlined"
                  onClick={handleTaskSelector}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  value={displayedTaskName}
                  size="small"
                  required
                />
                <Divider sx={{ height: 28, m: 1, color: 'black' }} orientation="vertical" />
                <IconButton aria-label="menu" onClick={handleTaskSelector}>
                  <Tooltip
                    open={driveOnCtx.task === undefined && chooseTaskDialog === undefined}
                    title={t('menetlevel.logbook.tooltip')}
                    placement="top-start"
                    arrow
                  >
                    <FormatListBulletedIcon
                      sx={{
                        boxShadow:
                          'rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset',
                      }}
                    />
                  </Tooltip>
                </IconButton>
              </Paper>
            )}
            <TextField
              fullWidth
              id="outlined-start-adornment"
              name="destination"
              label={t('menetlevel.logbook.form.destination')}
              placeholder="..."
              variant="outlined"
              value={details.destination}
              size="small"
              onChange={(e) => add(e)}
              onKeyDown={handleFormEnterKeyPress}
              inputProps={{
                enterKeyHint: 'done',
                tabIndex: -1,
              }}
              required
            />
            <TextField
              fullWidth
              id="outlined-start-adornment"
              name="productDescription"
              label={t('menetlevel.logbook.form.productDescription')}
              placeholder="..."
              variant="outlined"
              value={details.productDescription}
              size="small"
              onChange={(e) => add(e)}
              onKeyDown={handleFormEnterKeyPress}
              inputProps={{
                enterKeyHint: 'done',
                tabIndex: -1,
              }}
              required
            />
            <TextField
              fullWidth
              id="outlined-start-adornment"
              name="productWeight"
              label={t('menetlevel.logbook.form.productWeight')}
              variant="outlined"
              value={details.productWeight == NUMBER_DEFAULT_VALUE ? STRING_DEFAULT_VALUE : details.productWeight}
              placeholder="0"
              type="number"
              size="small"
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">tonna</InputAdornment>,
                },
              }}
              inputProps={{
                step: 0.5,
                enterKeyHint: 'done',
                tabIndex: -1,
              }}
              onChange={(e) => validateNumberAndAdd(e, 40)}
              onKeyDown={handleFormEnterKeyPress}
              required
            />
            <TextField
              fullWidth
              id="outlined-start-adornment"
              name="personCount"
              label={t('menetlevel.logbook.form.personCount')}
              variant="outlined"
              placeholder="0"
              value={details.personCount == NUMBER_DEFAULT_VALUE ? STRING_DEFAULT_VALUE : details.personCount}
              type="number"
              size="small"
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">fő</InputAdornment>,
                },
              }}
              onChange={(e) => validateNumberAndAdd(e, 5)}
              onKeyDown={handleFormEnterKeyPress}
              inputProps={{
                enterKeyHint: 'done',
                tabIndex: -1,
              }}
              required
            />
            <TextField
              fullWidth
              id="outlined-start-adornment"
              name="trailer"
              label={t('menetlevel.logbook.form.trailer')}
              placeholder="például: ABC-123, ABCD123"
              variant="outlined"
              value={details.trailer}
              size="small"
              onChange={(e) => add(e)}
              onKeyDown={handleFormEnterKeyPress}
              inputProps={{
                enterKeyHint: 'done',
                tabIndex: -1,
              }}
            />
            {TimeInputWithButtons(
              'workingHours',
              t('menetlevel.logbook.form.workingHours'),
              !details.workingHours || details.workingHours == TIME_NUMBER_DEFAULT_VALUE
                ? TIME_NUMBER_DEFAULT_VALUE
                : details.workingHours,
              (e) => convertTimeAndAdd(e, 'workingHours')
            )}
            {TimeInputWithButtons(
              'heating',
              t('menetlevel.logbook.form.heatingHours'),
              !details.heating || details.heating == TIME_NUMBER_DEFAULT_VALUE
                ? TIME_NUMBER_DEFAULT_VALUE
                : details.heating,
              (e) => convertTimeAndAdd(e, 'heating')
            )}
            <TextField
              fullWidth
              id="outlined-start-adornment"
              name="comment"
              label={t('menetlevel.logbook.form.comment')}
              placeholder="..."
              variant="outlined"
              value={details.comment}
              size="small"
              onChange={(e) => add(e)}
              onKeyDown={handleFormEnterKeyPress}
              inputProps={{
                enterKeyHint: 'done',
                tabIndex: -1,
              }}
            />
          </Box>
          {showSaveButton()}
        </Box>
        {chooseTaskDialog}
        {targetDialog}
      </div>
      <LoadingOverlay loading={loading} />
    </>
  );
}
